/** UK financial year helpers. FY27 = Apr 2026 – Mar 2027 (year ending March 2027). */

export function parseFyEndYear(fy: string): number | null {
  const match = /^FY(\d{2})$/i.exec(fy.trim());
  if (!match) return null;
  const short = Number(match[1]);
  return short >= 50 ? 1900 + short : 2000 + short;
}

export function fyCodeFromEndYear(endYear: number): string {
  return `FY${String(endYear % 100).padStart(2, '0')}`;
}

/** Display label e.g. FY27 → FY26/27 */
export function formatFyLabel(fy: string): string {
  const endYear = parseFyEndYear(fy);
  if (endYear == null) return fy;
  const startYear = endYear - 1;
  const startShort = String(startYear % 100).padStart(2, '0');
  const endShort = String(endYear % 100).padStart(2, '0');
  return `FY${startShort}/${endShort}`;
}

/** Financial year in progress for a given date (default: today). */
export function getCurrentFyCode(at: Date = new Date()): string {
  const month = at.getMonth();
  const year = at.getFullYear();
  const endYear = month >= 3 ? year + 1 : year;
  return fyCodeFromEndYear(endYear);
}

/** Apr 1 (startYear) → Mar 31 (endYear). FY code uses endYear (March year). */
export function getFyDateRange(fy: string): { start: Date; end: Date } | null {
  const endYear = parseFyEndYear(fy);
  if (endYear == null) return null;
  const startYear = endYear - 1;
  return {
    start: new Date(startYear, 3, 1),
    end: new Date(endYear, 2, 31, 23, 59, 59, 999)
  };
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Compare at month granularity (inclusive). */
export function monthInRange(monthDate: Date, rangeStart: Date, rangeEnd: Date): boolean {
  const ym = monthDate.getFullYear() * 12 + monthDate.getMonth();
  const fromYm = rangeStart.getFullYear() * 12 + rangeStart.getMonth();
  const toYm = rangeEnd.getFullYear() * 12 + rangeEnd.getMonth();
  return ym >= fromYm && ym <= toYm;
}

/* The warehouse's history starts with FY24 (Apr 2023). The list starts there
   rather than a fixed number of years back, so a year is never dropped from
   it as time moves on. */
const FIRST_DATA_FY_END_YEAR = 2024;

/** The first month any financial year holds data for, e.g. 2023-04-01. */
export const FIRST_DATA_MONTH = toIsoDate(new Date(FIRST_DATA_FY_END_YEAR - 1, 3, 1));

/**
 * Every financial year a date range touches, oldest first, e.g. Jan-25 –
 * Jun-26 → FY25, FY26, FY27. The dashboard loads one FY per request, so a
 * range reaching outside the selected FY needs each of these to show its
 * months at all.
 */
export function fyCodesInRange(fromIso: string, toIso: string): string[] {
  const fromEnd = parseFyEndYear(getCurrentFyCode(parseIsoDate(fromIso)));
  const toEnd = parseFyEndYear(getCurrentFyCode(parseIsoDate(toIso)));
  if (fromEnd == null || toEnd == null) return [];
  const codes: string[] = [];
  for (let endYear = Math.max(fromEnd, FIRST_DATA_FY_END_YEAR); endYear <= toEnd; endYear++) {
    codes.push(fyCodeFromEndYear(endYear));
  }
  return codes;
}

export function buildFySelectOptions(dataFyCodes: string[]): { label: string; value: string }[] {
  const codes = new Set<string>();
  const current = getCurrentFyCode();
  codes.add(current);

  const currentEnd = parseFyEndYear(current) ?? new Date().getFullYear();
  for (let endYear = FIRST_DATA_FY_END_YEAR; endYear <= currentEnd + 2; endYear++) {
    codes.add(fyCodeFromEndYear(endYear));
  }

  for (const raw of dataFyCodes) {
    const normalized = raw.trim().toUpperCase();
    if (/^FY\d{2}$/.test(normalized)) codes.add(normalized);
  }

  return [...codes]
    .sort((a, b) => b.localeCompare(a))
    .map((value) => ({ value, label: formatFyLabel(value) }));
}

export function getDefaultDateFilters(): { fy: string; dateFrom: string; dateTo: string } {
  const fy = getCurrentFyCode();
  const range = getFyDateRange(fy)!;
  return { fy, dateFrom: toIsoDate(range.start), dateTo: toIsoDate(range.end) };
}

/**
 * Current UK FY quarter for a date.
 * Q1 Apr–Jun · Q2 Jul–Sep · Q3 Oct–Dec · Q4 Jan–Mar
 */
export function getCurrentFyQuarterRange(at: Date = new Date()): {
  fy: string;
  quarter: 1 | 2 | 3 | 4;
  start: Date;
  end: Date;
} {
  const fy = getCurrentFyCode(at);
  const month = at.getMonth();
  const year = at.getFullYear();

  if (month >= 3 && month <= 5) {
    return {
      fy,
      quarter: 1,
      start: new Date(year, 3, 1),
      end: new Date(year, 6, 0, 23, 59, 59, 999)
    };
  }
  if (month >= 6 && month <= 8) {
    return {
      fy,
      quarter: 2,
      start: new Date(year, 6, 1),
      end: new Date(year, 9, 0, 23, 59, 59, 999)
    };
  }
  if (month >= 9 && month <= 11) {
    return {
      fy,
      quarter: 3,
      start: new Date(year, 9, 1),
      end: new Date(year, 12, 0, 23, 59, 59, 999)
    };
  }
  return {
    fy,
    quarter: 4,
    start: new Date(year, 0, 1),
    end: new Date(year, 3, 0, 23, 59, 59, 999)
  };
}

/** Current FY quarter window based on today's date (not a rolling 3-month lookback). */
export function getDefaultThreeMonthFilters(): { fy: string; dateFrom: string; dateTo: string } {
  const { fy, start, end } = getCurrentFyQuarterRange();
  return { fy, dateFrom: toIsoDate(start), dateTo: toIsoDate(end) };
}

/** e.g. Q2 · Jul–Sep 2026 */
export function formatCurrentQuarterLabel(at: Date = new Date()): string {
  const { quarter, start, end } = getCurrentFyQuarterRange(at);
  const startLabel = start.toLocaleString('en-GB', { month: 'short' });
  const endLabel = end.toLocaleString('en-GB', { month: 'short', year: 'numeric' });
  return `Q${quarter} · ${startLabel}–${endLabel}`;
}

/** Prior financial year code, e.g. FY27 → FY26. */
export function getPriorFyCode(fy: string): string | null {
  const endYear = parseFyEndYear(fy);
  if (endYear == null) return null;
  return fyCodeFromEndYear(endYear - 1);
}

/** Shift an ISO date by whole years (for YoY date-range alignment). */
export function shiftIsoDateByYears(iso: string, years: number): string {
  const d = parseIsoDate(iso);
  d.setFullYear(d.getFullYear() + years);
  return toIsoDate(d);
}
